
import i18next from 'i18next';

import MeetHourJS from '../base/lib-meet-hour';
import { MEDIA_TYPE } from '../base/media/constants';
import { getLocalParticipant, isLocalParticipantModerator, participantUpdated } from '../base/participants';
import { MiddlewareRegistry } from '../base/redux';
import { getTracksByMediaType } from '../base/tracks/functions';
import { NOTIFICATION_TIMEOUT_TYPE, showErrorNotification, showNotification } from '../notifications';
import { _TRANSCRIBER_JOINED } from '../transcribing/actionTypes';

import {
    ENDPOINT_MESSAGE_RECEIVED,
    SET_REQUESTING_SUBTITLES,
    TOGGLE_REQUESTING_SUBTITLES
} from './actionTypes';
import {
    announceClonedVoice,
    removeCachedTranscriptMessage,
    setRequestingSubtitles,
    setTranslatedAudioTrack,
    setVoiceCapability,
    setVoiceCatalog,
    setVoiceModeState,
    translatedVoiceUnavailable,
    updateTranscriptMessage
} from './actions';
import { notifyTranscriptionChunkReceived } from './functions';
import { getDefaultTranslationLanguage, getPrimaryLanguageCode } from './functions.any';
import logger from './logger';
import './subscriber';

/**
 * The type of json-message which indicates that json carries a
 * transcription result.
 */
const JSON_TYPE_TRANSCRIPTION_RESULT = 'transcription-result';

/**
 * The type of json-message which indicates that json carries a
 * translation result.
 */
const JSON_TYPE_TRANSLATION_RESULT = 'translation-result';

/**
 * The type of json-message which indicates voice mode state for participants.
 */
const JSON_TYPE_VOICE_MODE_STATE = 'voice-mode-state';

/**
 * The type of json-message which indicates clone/tts capability for participant.
 */
const JSON_TYPE_VOICE_CAPABILITIES = 'voice-capabilities';

/**
 * The type of json-message which indicates provider voice catalog payload.
 */
const JSON_TYPE_VOICE_CATALOG = 'voice-catalog';

/**
 * The type of json-message which announces a translated-voice audio track
 * becoming active/inactive for a speaker+language pair.
 */
const JSON_TYPE_TRANSLATED_AUDIO_TRACK = 'translated-audio-track';

/**
 * The type of json-message Jigasi sends, once per language, when translated voice cannot be produced in it.
 */
const JSON_TYPE_TRANSLATED_VOICE_UNAVAILABLE = 'translated-voice-unavailable';

/**
 * The type of json-message sent (by a participant, about themselves, or by a
 * moderator forcing another participant) requesting a voice mode change.
 * Broadcast to the whole room; only the *targeted* participant's own client
 * acts on it (self-publishing the resolved state - see
 * {@link P_NAME_VOICE_MODE}), consistent with XMPP presence only ever being
 * publishable by its own owner.
 */
const JSON_TYPE_VOICE_MODE_COMMAND = 'voice-mode-command';

/**
 * The local participant property (published via presence, like {@link
 * P_NAME_TRANSLATION_LANGUAGE}) holding this participant's own current voice
 * mode ({@code "original"} or {@code "translated"}). Published by the
 * targeted participant's own client in response to a {@link
 * JSON_TYPE_VOICE_MODE_COMMAND}, whether self-requested or moderator-forced,
 * so that server-side components with no visibility into room chat messages
 * (e.g. Jicofo) can still see each participant's resolved voice mode via
 * their presence, the same way {@link P_NAME_TRANSLATION_LANGUAGE} already
 * works.
 */
const P_NAME_VOICE_MODE = 'voice_mode';

/**
 * The local participant property which is used to set whether the local
 * participant wants to have a transcriber in the room.
 */
const P_NAME_REQUESTING_TRANSCRIPTION = 'requestingTranscription';

/**
 * The local participant property which is used to store the language
 * preference for translation for a participant.
 */
const P_NAME_TRANSLATION_LANGUAGE = 'translation_language';

/**
 * The dial command to use for starting a transcriber.
 */
const TRANSCRIBER_DIAL_NUMBER = 'jitsi_meet_transcribe';

/**
 * Stability factor for a transcription. We'll treat a transcript as stable
 * beyond this value.
 */
const STABLE_TRANSCRIPTION_FACTOR = 1.0;

/**
 * Middleware that catches actions related to transcript messages to be rendered
 * in {@link Captions}.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
    case ENDPOINT_MESSAGE_RECEIVED:
        return _endpointMessageReceived(store, next, action);

    case TOGGLE_REQUESTING_SUBTITLES: {
        const state = store.getState()['features/subtitles'];
        const toggledValue = !state._requestingSubtitles;

        _requestingSubtitlesChange(store, toggledValue, state._language);
        break;
    }
    case _TRANSCRIBER_JOINED: {
        // Reduce first so isTranscribing is already true when the request
        // below reaches _requestingSubtitlesChange: the transcriber is in the
        // room, it must not be dialed again.
        const result = next(action);
        const { transcription } = store.getState()['features/base/config'];
        const { confSettings } = store.getState()['features/base/jwt'];
        const { _requestingSubtitles, _language } = store.getState()['features/subtitles'];

        // This will enable Transcription for everyone in the room with dialog
        if (transcription?.autoCaptionOnTranscribe) {
            if (confSettings?.TRANSCRIPTION_QUOTA_EXCEEDED === 1) {
                if (isLocalParticipantModerator(store.getState())) {
                    store.dispatch(showErrorNotification(
                        {
                            descriptionKey: i18next.t('transcribing.transcriptionQuotaExceeded'),
                            titleKey: i18next.t('transcribing.transcriptionQuotaExceededTitle')
                        }));
                }
            } else if (_requestingSubtitles === false) {

                store.dispatch(setRequestingSubtitles(true, Boolean(_language), _language));
            }
        }

        // Every transcriber that joins starts with no idea which voice this participant cloned: the id lives in
        // this browser, Jigasi keeps it in per-session memory, and a room message never reaches someone who joins
        // after it was sent. This fires for a transcriber already present when we join too, and again on each
        // transcriber reconnect, so it covers every way of ending up on the default voice.
        store.dispatch(announceClonedVoice(getLocalParticipant(store.getState())?.id));

        return result;
    }
    case SET_REQUESTING_SUBTITLES: {
        // Turning captions on without a translation language picks one for the listener: their app language when
        // it can be translated into, otherwise English - and then they are told, rather than left wondering why
        // captions are in a language they did not choose.
        if (action.enabled && !action.language) {
            const { language, fallback } = getDefaultTranslationLanguage(i18next.language);

            if (fallback) {
                store.dispatch(showNotification({
                    titleKey: 'transcribing.translationLanguageFallbackTitle',
                    descriptionKey: 'transcribing.translationLanguageFallback',
                    descriptionArguments: { language: i18next.t(`languages:${getPrimaryLanguageCode(i18next.language)}`) }
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }

            // eslint-disable-next-line no-param-reassign
            action = {
                ...action,
                displaySubtitles: true,
                language
            };
        }

        _requestingSubtitlesChange(store, action.enabled, action.language);
        break;
    }
    }

    return next(action);
});

/**
 * Notifies the feature transcription that the action
 * {@code ENDPOINT_MESSAGE_RECEIVED} is being dispatched within a specific redux
 * store.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to
 * dispatch the specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code ENDPOINT_MESSAGE_RECEIVED}
 * which is being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _endpointMessageReceived(store: IStore, next: Function, action: AnyAction) {
    const { data: json } = action;
    const config = store.getState()['features/base/config'];
    const voiceModeEnabled = config?.transcription?.voiceMode?.enabled !== false;

    if (!voiceModeEnabled
        && (json?.type === JSON_TYPE_VOICE_MODE_STATE
            || json?.type === JSON_TYPE_VOICE_CAPABILITIES
            || json?.type === JSON_TYPE_VOICE_CATALOG
            || json?.type === JSON_TYPE_TRANSLATED_AUDIO_TRACK
            || json?.type === JSON_TYPE_VOICE_MODE_COMMAND)) {
        return next(action);
    }

    if (json?.type === JSON_TYPE_VOICE_MODE_STATE) {
        store.dispatch(setVoiceModeState(json));

        return next(action);
    }

    if (json?.type === JSON_TYPE_VOICE_MODE_COMMAND) {
        _publishVoiceModeIfTargetsLocalParticipant(store, json);

        return next(action);
    }

    if (json?.type === JSON_TYPE_VOICE_CAPABILITIES) {
        store.dispatch(setVoiceCapability(json));

        return next(action);
    }

    if (json?.type === JSON_TYPE_VOICE_CATALOG) {
        store.dispatch(setVoiceCatalog(json));

        return next(action);
    }

    if (json?.type === JSON_TYPE_TRANSLATED_AUDIO_TRACK) {
        const trackOpus = _handleTranslatedAudioTrack(store, json);

        store.dispatch(setTranslatedAudioTrack({ ...json,
            trackOpus }));
        _mirrorTranslatedTracksToParticipant(store, json.speaker_id);

        return next(action);
    }

    if (json?.type === JSON_TYPE_TRANSLATED_VOICE_UNAVAILABLE) {
        store.dispatch(translatedVoiceUnavailable(json.language));

        return next(action);
    }

    if (![ JSON_TYPE_TRANSCRIPTION_RESULT, JSON_TYPE_TRANSLATION_RESULT ].includes(json?.type)) {
        return next(action);
    }

    const { dispatch, getState } = store;
    const state = getState();
    const language
        = state['features/base/conference'].conference
            ?.getLocalParticipantProperty(P_NAME_TRANSLATION_LANGUAGE);
    const { dumpTranscript, skipInterimTranscriptions } = state['features/base/config'].testing ?? {};

    const transcriptMessageID = json.message_id;
    const { name, id, avatar_url: avatarUrl } = json.participant;
    const participant = {
        avatarUrl,
        id,
        name
    };

    let newTranscriptMessage: ITranscriptMessage | undefined;

    if (json.type === JSON_TYPE_TRANSLATION_RESULT) {
        if (json.language !== language) {
            return next(action);
        }

        // Attached to the sentence's existing line, which the transcription result created when it arrived - so a
        // translation can never reorder lines, however late it comes. `translated: false` means Jigasi kept the
        // original text (same language, or a translation that was unsupported or failed): the line already shows
        // it, in its own language and script, so nothing is replaced.
        const existingMessage = state['features/subtitles']._transcriptMessages.get(transcriptMessageID);
        const text = json.text?.trim();

        if (!text || (existingMessage && json.translated === false)) {
            return next(action);
        }

        dispatch(updateTranscriptMessage(transcriptMessageID, existingMessage
            ? { ...existingMessage,
                translated: text }
            : { clearTimeOut: undefined,
                final: text,
                participant }));

        return next(action);
    } else if (json.type === JSON_TYPE_TRANSCRIPTION_RESULT) {
        // Displays interim and final results without any translation if
        // translations are disabled.

        const { text } = json.transcript[0];

        // First, notify the external API.
        if (!(json.is_interim && skipInterimTranscriptions)) {
            const txt: any = {};

            if (!json.is_interim) {
                txt.final = text;
            } else if (json.stability > STABLE_TRANSCRIPTION_FACTOR) {
                txt.stable = text;
            } else {
                txt.unstable = text;
            }

            notifyTranscriptionChunkReceived({
                transcriptMessageID,
                language: json.language,
                participant,
                text: txt,
                _store: store
            });

            if (navigator.product !== 'ReactNative' && !json.is_interim && dumpTranscript) {

                // Dump transcript in a <transcript> element for debugging purposes.
                try {
                    let elem = document.body.getElementsByTagName('transcript')[0];

                    if (!elem) {
                        elem = document.createElement('transcript');
                        document.body.appendChild(elem);
                    }

                    elem.append(`${new Date(json.timestamp).toISOString()} ${participant.name}: ${text}`);
                } catch {
                    // Ignored.
                }
            }
        }

        // If the user is not requesting transcriptions just bail. With translation on, every final sentence also
        // arrives as a translation-result in the listener's language - carrying the original words when they were
        // already in that language or could not be translated - so only those are shown: drawing the transcription
        // first flashed the original before the translation replaced it.
        if (!language || state['features/base/config'].transcription?.translationEnabled) {
            return next(action);
        }

        if (json.is_interim && skipInterimTranscriptions) {
            return next(action);
        }

        // We update the previous transcript message with the same
        // message ID or adds a new transcript message if it does not
        // exist in the map.
        const existingMessage = state['features/subtitles']._transcriptMessages.get(transcriptMessageID);

        newTranscriptMessage = {
            clearTimeOut: existingMessage?.clearTimeOut,
            participant,
            translated: existingMessage?.translated
        };

        // If this is final result, update the state as a final result
        // and start a count down to remove the subtitle from the state
        if (!json.is_interim) {
            newTranscriptMessage.final = text;
        } else if (json.stability > STABLE_TRANSCRIPTION_FACTOR) {
            // If the message has a high stability, we can update the
            // stable field of the state and remove the previously
            // unstable results
            newTranscriptMessage.stable = text;
        } else {
            // Otherwise, this result has an unstable result, which we
            // add to the state. The unstable result will be appended
            // after the stable part.
            newTranscriptMessage.unstable = text;
        }
    }

    if (newTranscriptMessage) {
        if (newTranscriptMessage.final) {
            const cachedTranscriptMessage
                = state['features/subtitles']._cachedTranscriptMessages?.get(transcriptMessageID);

            if (cachedTranscriptMessage) {
                const cachedText = (cachedTranscriptMessage.stable || cachedTranscriptMessage.unstable)?.trim();
                const newText = newTranscriptMessage.final;

                if (cachedText && cachedText.length > 0 && newText && newText.length > 0
                    && newText.toLowerCase().startsWith(cachedText.toLowerCase())) {
                    newTranscriptMessage.final = newText.slice(cachedText.length)?.trim();
                }
                dispatch(removeCachedTranscriptMessage(transcriptMessageID));

                if (!newTranscriptMessage.final || newTranscriptMessage.final.length === 0) {
                    return next(action);
                }
            }
        }

        dispatch(updateTranscriptMessage(transcriptMessageID, newTranscriptMessage));
    }

    return next(action);
}

/**
 * Handles a {@code voice-mode-command}, broadcast to the whole room whenever
 * a participant requests (for themselves) or a moderator forces (on another
 * participant) a voice mode change. Only the *targeted* participant's own
 * client acts on it: it publishes the resolved voice mode onto its own
 * presence ({@link P_NAME_VOICE_MODE}), since XMPP presence can only ever be
 * published by its own owner - a moderator forcing a change on someone else
 * cannot publish presence on that other participant's behalf.
 *
 * This is what lets server-side components with no visibility into room
 * chat/endpoint messages (notably Jicofo, which never sees this command)
 * still observe each participant's resolved voice mode via presence, the
 * same mechanism already used for {@link P_NAME_TRANSLATION_LANGUAGE}.
 *
 * Jigasi's own existing handling of this same command (updating its
 * in-memory participant model, broadcasting {@code voice-mode-state}) is
 * unaffected by this - this is an additional, independent consumer of the
 * same message, not a replacement.
 *
 * @param {Store} store - The redux store.
 * @param {Object} json - The voice-mode-command payload ({@code
 * participant_id}, {@code voice_mode}, {@code force}).
 * @private
 * @returns {void}
 */
function _publishVoiceModeIfTargetsLocalParticipant({ getState }: IStore, json: Object) {
    const { participant_id: targetId, voice_mode: voiceMode } = json;

    if (!targetId || !voiceMode) {
        return;
    }

    const state = getState();
    const localParticipant = getLocalParticipant(state);

    if (!localParticipant || localParticipant.id !== targetId) {
        return;
    }

    const conference = state['features/base/conference']?.conference;

    conference?.setLocalParticipantProperty(P_NAME_VOICE_MODE, voiceMode);
}

/**
 * Attaches/updates the resolved translated-voice audio track reference on
 * the originating speaker's MHParticipant (not the transcriber, which is who
 * actually sent this endpoint message) so it survives as
 * {@code participant.getTranslatedTracks()} for UI/playout to read.
 *
 * The synthesized audio for every speaker/language is currently carried on
 * the transcriber's single outgoing audio source, so {@code source_ssrc}
 * always resolves to the transcriber's remote track; per-language sources
 * are a tracked follow-up (see INWORLD_TTS_IMPLEMENTATION_REFERENCE.md §9).
 *
 * @param {Store} store - The redux store.
 * @param {Object} json - The translated-audio-track payload.
 * @private
 * @returns {string|undefined} the resolved trackOpus (remote track source
 * name), so the caller can mirror the same value into the redux-side
 * payload it dispatches.
 */
function _handleTranslatedAudioTrack({ getState }: IStore, json: Object) {
    const { speaker_id: speakerId, language, source_ssrc: sourceSsrc, active } = json;

    if (!speakerId || !language) {
        return undefined;
    }

    const state = getState();
    const conference = state['features/base/conference']?.conference;
    const participant = conference?.getParticipantById(speakerId);

    let trackOpus;

    if (typeof sourceSsrc === 'number') {
        // Prefer the lib RTC view: it includes the (hidden) transcriber's audio
        // track that carries the synthesized speech, which features/base/tracks
        // can filter out for hidden participants - in which case the base/tracks
        // lookup alone would leave trackOpus undefined. Fall back to base/tracks.
        const libTrack = conference?.rtc?.getRemoteTracks?.(MEDIA_TYPE.AUDIO)
            ?.find(t => t.getSSRC?.() === sourceSsrc);

        if (libTrack) {
            trackOpus = libTrack.getSourceName?.();
        } else {
            const track = getTracksByMediaType(state['features/base/tracks'], MEDIA_TYPE.AUDIO)
                .find(t => t.mhTrack?.getSSRC?.() === sourceSsrc);

            trackOpus = track?.mhTrack?.getSourceName?.();
        }
    }

    if (participant?.addOrUpdateTranslatedTrack) {
        participant.addOrUpdateTranslatedTrack({
            trackOpus,
            translateLanguage: language,
            active: Boolean(active)
        });
    }

    return trackOpus;
}

/**
 * Mirrors a speaker's translated-voice audio tracks onto their
 * {@code features/base/participants} entry (as a {@code translatedTracks}
 * array), so the data is visible on the participant object itself - matching
 * the shape UI code and consumers expect - in addition to the authoritative
 * per-speaker map in {@code features/subtitles} and the lib MHParticipant.
 *
 * Reads the already-updated subtitles state (dispatch of
 * {@code setTranslatedAudioTrack} is synchronous and has completed by the time
 * this is called), so the mirrored array always reflects every language
 * currently tracked for the speaker, not just the one that changed.
 *
 * @param {Store} store - The redux store.
 * @param {string} speakerId - The originating speaker's participant id.
 * @private
 * @returns {void}
 */
function _mirrorTranslatedTracksToParticipant({ dispatch, getState }: IStore, speakerId: string) {
    if (!speakerId) {
        return;
    }

    const byLanguage = getState()['features/subtitles']._translatedAudioTracksByParticipant.get(speakerId);
    const translatedTracks = byLanguage
        ? Array.from(byLanguage.values()).map(track => {
            return {
                trackOpus: track.trackOpus,
                translateLanguage: track.language,
                active: Boolean(track.active)
            };
        })
        : [];

    dispatch(participantUpdated({
        id: speakerId,
        translatedTracks
    }));
}

/**
 * Toggle the local property 'requestingTranscription'. This will cause Jicofo
 * and Jigasi to decide whether the transcriber needs to be in the room.
 *
 * @param {Store} store - The redux store.
 * @param {boolean} enabled - Whether subtitles should be enabled or not.
 * @param {string} language - The language to use for translation.
 * @private
 * @returns {void}
 */
function _requestingSubtitlesChange({ dispatch, getState }, enabled, language) {
    const state = getState();
    const { conference } = state['features/base/conference'];
    const { isTranscribing } = state['features/transcribing'];

    conference?.setLocalParticipantProperty(
    P_NAME_REQUESTING_TRANSCRIPTION,
    enabled
    );

    // Only moderators may dial (jicofo answers anyone else with forbidden),
    // and never while the transcriber is already running. Everyone else is
    // served by the requestingTranscription property set above.
    const isTranscriberRunning = isTranscribing
        || conference?.getTranscriptionStatus() === MeetHourJS.constants.transcriptionStatus.ON;

    if (enabled && !isTranscriberRunning && isLocalParticipantModerator(state)) {
        const confSettings = state['features/base/jwt']?.confSettings
            || state['features/base/jwt']?.meetingDetails?.settings;
        const isTranscriptionAllowed
            = Boolean(confSettings?.ENABLE_TRANSCRIPTION_MODE || Number(confSettings?.AUTO_START_TRANSCRIBE) === 1);
        const featureAllowed = isTranscriptionAllowed;

        if (featureAllowed) {
            conference?.dial(TRANSCRIBER_DIAL_NUMBER).catch(e => {
                // e is the raw XMPP DOM stanza - logging the whole node floods the error feed.
                // Pull just the human-readable text node (mirrors what lib-meet-hour's ChatRoom does).
                const reason = e && typeof e.querySelector === 'function'
                    ? e.querySelector('error > text')?.textContent
                        || e.querySelector('error')?.getAttribute('type')
                        || e.getAttribute('type')
                    : undefined;

                logger.error('Error dialing transcrib', reason, e);

                // let's back to the correct state
                dispatch(setRequestingSubtitles(false, false, null));
            });
        }
    }

    if (enabled && language) {
        conference?.setLocalParticipantProperty(
      P_NAME_TRANSLATION_LANGUAGE,
      language.replace('translation-languages:', '')
        );
    }
}

